import React, { useMemo, useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import "./PlateDetails.scss";
import { Link } from "react-router-dom";
import { revokePlateStatus, suspendPlate } from "../../../services/platesService";
import {
  getPlateCaption,
  getPlateDisplayImageUrl,
  getPlateLocationCountry,
  getPlateLocationState,
  getPlateNumber,
  getProfileUser,
  mapPlateStatusLabel,
} from "../../../utils/platesProfileHelpers";

function PlateDetails() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const plateData = location.state?.plateData;
  const revokeModalRef = useRef(null);
  const suspendModalRef = useRef(null);

  const plateEntry = plateData?.plateEntry;
  const plateId =
    location.state?.plateId || plateEntry?._id || plateData?._id || plateData?.id;

  // State for revoke form
  const [revokeSubject, setRevokeSubject] = useState("");
  const [revokeMessage, setRevokeMessage] = useState("");

  // State for suspend form
  const [suspendDuration, setSuspendDuration] = useState("");
  const [suspendReason, setSuspendReason] = useState("");

  // Revoke reasons
  const revokeReasons = [
    "False or duplicate claim",
    "Ownership dispute",
    "Fraudulent documentation",
    "Other",
  ];

  // Transform API data to match display structure
  const displayData = useMemo(() => {
    if (!plateData) {
      return null;
    }

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
        const timeStr = date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return `${dateStr} | ${timeStr}`;
      } catch (e) {
        return dateString;
      }
    };

    const user = getProfileUser(plateData);
    const userName =
      plateData.businessName ||
      plateData.profilename ||
      plateData.username ||
      user.name ||
      user.fullName ||
      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
      user.email ||
      "N/A";

    const userEmail = plateData.businessEmail || user.email || "N/A";
    const userId = user._id || user.id || "N/A";

    // Map KYC status
    const kycStatusMap = {
      verified: "Verified",
      unverified: "Unverified",
      pending: "Pending",
    };
    const kycStatus =
      kycStatusMap[user.kycVerification?.toLowerCase()] ||
      user.kycVerification?.charAt(0).toUpperCase() +
        user.kycVerification?.slice(1) ||
      "Unverified";

    // Map profile visibility
    const profileType =
      user.profileVisibility?.charAt(0).toUpperCase() +
        user.profileVisibility?.slice(1) || "Public";

    const plateStatus = mapPlateStatusLabel(
      plateEntry?.status ?? plateData.status
    );

    const plateImage =
      getPlateDisplayImageUrl(plateEntry, plateData) ||
      process.env.PUBLIC_URL + "/images/plate.svg";
    const profileImage =
      plateData.profilePictureUrl ||
      user.profilePicture ||
      user.profilePictureUrl ||
      process.env.PUBLIC_URL + "/images/user.svg";
    const coverImage =
      plateData.coverPictureUrl ||
      user.coverPicture ||
      user.coverPictureUrl ||
      null;

    return {
      sections: [
        {
          title: "Plate Information",
          items: [
            {
              label: "Plate Number",
              value: getPlateNumber(plateEntry, plateData),
            },
            {
              label: "Claim Status",
              value: plateStatus,
              type: "status",
            },
            {
              label: "Approval Date",
              value: formatDate(
                plateEntry?.updatedAt || plateData.updatedAt || plateData.createdAt
              ),
            },
            {
              label: "State / Region",
              value: getPlateLocationState(plateEntry, plateData) || "N/A",
            },
            {
              label: "Country",
              value: getPlateLocationCountry(plateEntry, plateData) || "N/A",
            },
            {
              label: "Plate entry ID",
              value: plateEntry?._id || plateData._id || plateData.claimId || "N/A",
            },
            {
              label: "Car Make",
              value: plateEntry?.carMake || plateData.carMake || "N/A",
            },
            {
              label: "Car Model",
              value: plateEntry?.carModel || plateData.carModel || "N/A",
            },
            {
              label: "Year",
              value: plateEntry?.year || plateData.year || "N/A",
            },
            {
              label: "Color",
              value: plateEntry?.color || plateData.color || "N/A",
            },
          ],
        },
        {
          title: "Media",
          items: [
            {
              label: "Plate Photo",
              image: plateImage,
              type: "image",
            },
            ...(coverImage
              ? [
                  {
                    label: "Cover Photo",
                    image: coverImage,
                    type: "image",
                  },
                ]
              : []),
          ],
        },
        {
          title: "User Details",
          items: [
            {
              label: "Profile Picture",
              image: profileImage,
              type: "image",
            },
            { label: "User Name", value: userName },
            { label: "User ID", value: userId },
            { label: "Email", value: userEmail },
            {
              label: "KYC Status",
              value: kycStatus,
              type: kycStatus === "Verified" ? "status-approved" : "status",
            },
            { label: "Profile Type", value: profileType },
            {
              label: "Username",
              value: plateData.username || user.username || "N/A",
            },
            {
              label: "Bio",
              value: plateData.bio || user.bio || "N/A",
            },
          ],
        },
        ...(getPlateCaption(plateEntry) ||
        plateData.caption ||
        plateEntry?.tags?.length > 0 ||
        plateData.tags?.length > 0
          ? [
              {
                title: "Additional Information",
                items: [
                  ...(getPlateCaption(plateEntry) || plateData.caption
                    ? [
                        {
                          label: "Caption",
                          value:
                            getPlateCaption(plateEntry) || plateData.caption,
                        },
                      ]
                    : []),
                  ...(plateEntry?.tags?.length > 0 || plateData.tags?.length > 0
                    ? [
                        {
                          label: "Tags",
                          value: Array.isArray(plateEntry?.tags || plateData.tags)
                            ? (plateEntry?.tags || plateData.tags).join(", ")
                            : plateEntry?.tags || plateData.tags,
                        },
                      ]
                    : []),
                  ...(plateData.drivingStyleTags?.length > 0
                    ? [
                        {
                          label: "Driving Style Tags",
                          value: plateData.drivingStyleTags.join(", "),
                        },
                      ]
                    : []),
                  ...(plateData.carTypeTags?.length > 0
                    ? [
                        {
                          label: "Car Type Tags",
                          value: plateData.carTypeTags.join(", "),
                        },
                      ]
                    : []),
                  ...(plateData.lifeStyleTags?.length > 0
                    ? [
                        {
                          label: "Lifestyle Tags",
                          value: plateData.lifeStyleTags.join(", "),
                        },
                      ]
                    : []),
                ],
              },
            ]
          : []),
      ],
    };
  }, [plateData, plateEntry]);

  // Revoke plate status mutation
  const revokePlateMutation = useMutation({
    mutationFn: ({ id, revokeData }) => revokePlateStatus(id, revokeData),
    onSuccess: (data) => {
      toast.success(
        data?.message || "Plate verification revoked successfully",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );

      // Reset form
      setRevokeSubject("");
      setRevokeMessage("");

      // Close modal
      if (revokeModalRef.current) {
        const modalElement = revokeModalRef.current;
        const modal = window.bootstrap?.Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        } else {
          const $modal = window.$ ? window.$(modalElement) : null;
          if ($modal) {
            $modal.modal("hide");
          }
        }
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      queryClient.invalidateQueries({ queryKey: ["plates-by-status"] });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to revoke plate verification";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
      });
    },
  });

  const handleRevokeVerification = () => {
    if (!plateId) {
      toast.error("Plate ID is missing");
      return;
    }

    // Validate required fields
    if (!revokeSubject.trim()) {
      toast.error("Please select a revocation reason", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (!revokeMessage.trim()) {
      toast.error("Please enter a revocation message", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    revokePlateMutation.mutate({
      id: plateId,
      revokeData: {
        subject: revokeSubject.trim(),
        message: revokeMessage.trim(),
      },
    });
  };

  const handleRevokeSubjectChange = (subject) => {
    setRevokeSubject(subject);
  };

  // Suspend plate mutation
  const suspendPlateMutation = useMutation({
    mutationFn: ({ id, suspendData }) => suspendPlate(id, suspendData),
    onSuccess: (data) => {
      toast.success(data?.message || "Plate suspended successfully", {
        position: "top-right",
        autoClose: 3000,
      });

      // Reset form
      setSuspendDuration("");
      setSuspendReason("");

      // Close modal
      if (suspendModalRef.current) {
        const modalElement = suspendModalRef.current;
        const modal = window.bootstrap?.Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        } else {
          const $modal = window.$ ? window.$(modalElement) : null;
          if ($modal) {
            $modal.modal("hide");
          }
        }
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      queryClient.invalidateQueries({ queryKey: ["plates-by-status"] });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to suspend plate";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
      });
    },
  });

  const handleSuspendPlate = () => {
    if (!plateId) {
      toast.error("Plate ID is missing");
      return;
    }

    // Validate required fields
    if (!suspendDuration.trim()) {
      toast.error("Please enter a suspension duration", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Validate duration is a number
    const durationNum = parseInt(suspendDuration.trim());
    if (isNaN(durationNum) || durationNum <= 0) {
      toast.error("Please enter a valid duration (number of days)", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (!suspendReason.trim()) {
      toast.error("Please enter a suspension reason", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    suspendPlateMutation.mutate({
      id: plateId,
      suspendData: {
        duration: durationNum,
        reason: suspendReason.trim(),
      },
    });
  };

  // Reset forms when modals are closed
  useEffect(() => {
    const revokeModalElement = revokeModalRef.current;
    const suspendModalElement = suspendModalRef.current;

    const handleRevokeModalHidden = () => {
      setRevokeSubject("");
      setRevokeMessage("");
    };

    const handleSuspendModalHidden = () => {
      setSuspendDuration("");
      setSuspendReason("");
    };

    if (revokeModalElement) {
      revokeModalElement.addEventListener(
        "hidden.bs.modal",
        handleRevokeModalHidden
      );
    }

    if (suspendModalElement) {
      suspendModalElement.addEventListener(
        "hidden.bs.modal",
        handleSuspendModalHidden
      );
    }

    return () => {
      if (revokeModalElement) {
        revokeModalElement.removeEventListener(
          "hidden.bs.modal",
          handleRevokeModalHidden
        );
      }
      if (suspendModalElement) {
        suspendModalElement.removeEventListener(
          "hidden.bs.modal",
          handleSuspendModalHidden
        );
      }
    };
  }, []);

  // Redirect if no data
  if (!plateData) {
    return (
      <React.Fragment>
        <header className="plate-detail-panel-header items-center gap-3 flex content-justify-between">
          <div className="left-side">
            <Link to="/plates-management/approved-plates">
              <img src={process.env.PUBLIC_URL + "/images/back.svg"} alt="back" />
            </Link>
            <h4 className="title">Plate Details</h4>
          </div>
        </header>
        <div className="plateDetails">
          <div className="card">
            <p>No plate data available. Please select a plate from the list.</p>
          </div>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <header className="plate-detail-panel-header items-center gap-3 flex content-justify-between">
        <div className="left-side">
          <Link to="/plates-management/approved-plates">
            <img src={process.env.PUBLIC_URL + "/images/back.svg"} alt="back" />
          </Link>
          <h4 className="title">Plate Details</h4>
        </div>

        <div className="right-side">
          <button
            className="revoke"
            data-bs-toggle="modal"
            data-bs-target="#revokeClaimModal"
          >
            Revoke Verification
          </button>
          <button
            className="suspend"
            data-bs-toggle="modal"
            data-bs-target="#suspendInfoModal"
          >
            Suspend / Blacklist Plate
          </button>
        </div>
      </header>

      <div className="plateDetails">
        <div className="row">
          {displayData.sections.map((section, index) => {
            let colSize = "12";

            if (section.title === "Plate Information") {
              colSize = "8";
            } else if (section.title === "Media") {
              colSize = "4";
            }

            return (
              <div
                key={index}
                className={`col-lg-${
                  section.title === "Media" ? "12" : "12"
                } col-xl-${colSize} `}
              >
                <div className="card">
                  <h4>{section.title}</h4>

                  <div className="inner-content">
                    <div className="row">
                      {section.items.map((item, i) => (
                        <div
                          key={i}
                          className={`col-lg-${
                            section.title === "Media" ? "12" : "4"
                          } col-xl-${section.title === "Media" ? "12" : "4"} `}
                        >
                          <div className="content-wrapper">
                            <img src={process.env.PUBLIC_URL + "/images/user-icon.svg"} alt="icon" />

                            <div className="content">
                              <h5>{item.label}</h5>

                              {/* STATUS BADGE */}
                              {item.type === "status" && (
                                <p
                                  className={`status-badge ${
                                    item.value?.toLowerCase() || "pending"
                                  }`}
                                >
                                  <span className="dot"></span> {item.value}
                                </p>
                              )}

                              {item.type === "status-approved" && (
                                <p className="status-badge approved">
                                  <span className="dot"></span> {item.value}
                                </p>
                              )}

                              {/* IMAGE TYPE */}
                              {item.type === "image" && (
                                <div className="car-plate">
                                  <img
                                    src={item.image}
                                    alt={item.label || "image"}
                                    onError={(e) => {
                                      if (item.label?.includes("Plate")) {
                                        e.target.src = process.env.PUBLIC_URL + "/images/plate.svg";
                                      } else if (
                                        item.label?.includes("Profile")
                                      ) {
                                        e.target.src = process.env.PUBLIC_URL + "/images/user.svg";
                                      } else {
                                        e.target.src = process.env.PUBLIC_URL + "/images/plate.svg";
                                      }
                                    }}
                                  />
                                </div>
                              )}

                              {/* DEFAULT TEXT */}
                              {!item.type && <p>{item.value}</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* revoke Claim Modal */}
      <div
        ref={revokeModalRef}
        className="modal fade"
        id="revokeClaimModal"
        tabIndex="-1"
        aria-labelledby="revokeClaimModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="revokeClaimModalLabel">
                Revoke Verification
              </h5>
            </div>

            <div className="modal-body">
              <h3>
                Are you sure you want to revoke verification for plate{" "}
                {getPlateNumber(plateEntry, plateData)}
                ?
              </h3>
              <label className="form-label">
                This will remove the shield badge from the user's profile and
                move the record to "Revoked" in Audit Logs.
              </label>
              <label className="form-label">Select Reason</label>

              {/* Radio buttons - only one selectable */}
              {revokeReasons.map((reason, index) => (
                <div key={index} className="form-check mb-2">
                  <input
                    type="radio"
                    name="revokeReason"
                    id={`revokeReason${index + 1}`}
                    checked={revokeSubject === reason}
                    onChange={() => handleRevokeSubjectChange(reason)}
                    required
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`revokeReason${index + 1}`}
                  >
                    {reason}
                  </label>
                </div>
              ))}

              {/* Textarea */}
              <label className="form-label mt-3">Message</label>
              <textarea
                className="form-control"
                rows="5"
                placeholder="Write here"
                value={revokeMessage}
                onChange={(e) => setRevokeMessage(e.target.value)}
                required
              ></textarea>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
                disabled={revokePlateMutation.isPending}
              >
                Close
              </button>

              <button
                type="button"
                className="red-btn"
                onClick={handleRevokeVerification}
                disabled={revokePlateMutation.isPending}
              >
                {revokePlateMutation.isPending
                  ? "Revoking..."
                  : "Revoke Verification"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* suspend More Information Modal */}
      <div
        ref={suspendModalRef}
        className="modal fade"
        id="suspendInfoModal"
        tabIndex="-1"
        aria-labelledby="suspendInfoModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="suspendInfoModalLabel">
                Suspend Plate Ownership
              </h5>
            </div>

            <div className="modal-body">
              <h3>
                Temporarily disable all activity linked to this plate for{" "}
                {suspendDuration || "30"} days.
              </h3>
              <label className="form-label">
                During suspension, users cannot post or tag this plate.
              </label>
              <br />
              <label className="form-label">Duration (in days)</label>
              <input
                type="number"
                className="form-control"
                placeholder="30"
                value={suspendDuration}
                onChange={(e) => setSuspendDuration(e.target.value)}
                min="1"
                required
              />
              <label className="form-label mt-3">Reason for suspension</label>

              <textarea
                className="form-control"
                rows="3"
                placeholder="Write here"
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                required
              ></textarea>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
                disabled={suspendPlateMutation.isPending}
              >
                Close
              </button>

              <button
                type="button"
                className="submit-btn"
                onClick={handleSuspendPlate}
                disabled={suspendPlateMutation.isPending}
              >
                {suspendPlateMutation.isPending
                  ? "Suspending..."
                  : "Suspend Plate"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default PlateDetails;
